import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Users, CalendarDays, Plus, Link2, Info } from 'lucide-react';
import api from '../../api/axios';
import Loader from '../../components/Loader';


function countDaysInCurrentMonth(vacations) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);

  let total = 0;
  (vacations || []).forEach(v => {
    if (v.vacation_type !== 'wypoczynkowy') return; // tylko wypoczynkowy, tak jak "Użyte"

    const start = new Date(v.start_date);
    const end = new Date(v.end_date);
    const rangeStart = start < monthStart ? monthStart : start;
    const rangeEnd = end > monthEnd ? monthEnd : end;

    let cur = new Date(rangeStart);
    while (cur <= rangeEnd) {
      const day = cur.getDay();
      if (day !== 0 && day !== 6) total += 1; // pomiń weekendy
      cur.setDate(cur.getDate() + 1);
    }
  });
  return total;
}

function EmployerHome() {
  const navigate = useNavigate();
  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  const [slots, setSlots] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const [slotsResult, managedResult, linkedResult] = await Promise.allSettled([
          api.get('/employer/slots/'),
          api.get('/employer/workers/'),
          api.get('/employer/linked-workers/accepted/'),
        ]);

        if (slotsResult.status === 'fulfilled') setSlots(slotsResult.value.data);

        const managed = managedResult.status === 'fulfilled'
          ? (Array.isArray(managedResult.value.data) ? managedResult.value.data : (managedResult.value.data?.results ?? []))
              .map(w => ({ ...w, source: 'managed' }))
          : [];

        const linkedBase = linkedResult.status === 'fulfilled' ? (linkedResult.value.data || []) : [];

        // Dociągnij zaakceptowane urlopy dla każdego połączonego pracownika (do statystyki miesięcznej)
        const linkedWithVacations = await Promise.all(
          linkedBase.map(async (link) => {
            let vacations = [];
            try {
              const res = await api.get(`/employer/linked-workers/${link.id}/vacation-calendar/`);
              vacations = res.data?.vacations || [];
            } catch (err) {
              console.error(err);
            }
            return {
              id: link.id,
              source: 'linked',
              worker_type: link.worker_is_apprentice ? 'apprentice' : 'employee',
              first_name: link.worker_first_name,
              last_name: link.worker_last_name,
              vacation_days_total: link.vacation_days_total,
              vacation_days_used: link.vacation_days_used,
              vacation_days_available: link.vacation_days_available,
              vacations,
            };
          })
        );

        setWorkers([...managed, ...linkedWithVacations]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <Loader />;

  const isFreePlan = (slots?.total_worker_slots || 1) <= 1;

  return (
    <div className="py-4">
      <div className="app-page-title">
        <h1>Witaj, {userInfo?.name}</h1>
        <p>{userInfo?.company_name || 'Panel pracodawcy SIGMA'}</p>
      </div>

      {/* LIMIT PLANU DARMOWEGO */}
      {isFreePlan && (
        <div className="pub-card d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4" style={{ padding: '16px 20px' }}>
          <div className="d-flex align-items-center gap-2 pub-muted">
            <Info size={18} strokeWidth={1.75} />
            <span>Plan darmowy pozwala dodać <b>1 pracownika</b>. Potrzebujesz więcej?</span>
          </div>
          <button type="button" className="pub-btn" onClick={() => navigate('/employer-app-pricing')}>
            Zobacz plany
          </button>
        </div>
      )}

      {/* STATYSTYKI */}
      <Row className="g-3 mb-4">
        {[
          {
            icon: <Users size={20} strokeWidth={1.75} />,
            label: 'Pracownicy',
            value: `${slots?.used_worker_slots || 0} / ${slots?.total_worker_slots || 1}`,
            onClick: () => navigate('/employer-employees'),
          },
          {
            icon: <CalendarDays size={20} strokeWidth={1.75} />,
            label: 'Dni urlopu w tym miesiącu',
            value: workers.reduce((sum, w) => sum + countDaysInCurrentMonth(w.vacations), 0),
            onClick: () => navigate('/employer-vacations'),
          },
        ].map(stat => (
          <Col key={stat.label} xs={12} sm={6} lg={4}>
            <button type="button" className="app-stat w-100 text-start" onClick={stat.onClick}>
              <span className="d-flex align-items-center gap-2 mb-2">{stat.icon}{stat.label}</span>
              <b>{stat.value}</b>
            </button>
          </Col>
        ))}
      </Row>

      {/* SZYBKIE AKCJE */}
      <div className="pub-actions mb-5">
        <button type="button" className="pub-btn" onClick={() => navigate('/employer-employees')}>
          <Plus size={16} className="me-2" />Dodaj pracownika
        </button>
        <button type="button" className="pub-link" style={{ background: 'none', border: 'none' }} onClick={() => navigate('/employer-vacations')}>
          Zarządzaj urlopami
        </button>
      </div>

      {/* LISTA PRACOWNIKÓW — PODGLĄD */}
      {workers.length > 0 && (
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
            <h2 className="pub-h3 mb-0">Twoi pracownicy</h2>
            <button type="button" className="pub-link" style={{ background: 'none', border: 'none' }} onClick={() => navigate('/employer-employees')}>
              Zobacz wszystkich
            </button>
          </div>
          <Row className="g-3">
            {workers.slice(0, 4).map(worker => (
              <Col key={`${worker.source}-${worker.id}`} xs={12} sm={6} lg={3}>
                <button type="button" className="pub-card w-100 text-start" style={{ padding: 16 }} onClick={() => navigate('/employer-employees')}>
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%', background: 'var(--pub-navy)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, flexShrink: 0,
                    }}>
                      {worker.first_name?.[0]}{worker.last_name?.[0]}
                    </div>
                    <div>
                      <div className="fw-medium d-flex align-items-center gap-1">
                        {worker.first_name} {worker.last_name}
                        {worker.source === 'linked' && <Link2 size={12} className="pub-small" aria-label="Konto połączone" />}
                      </div>
                      <div className="pub-small">
                        {worker.worker_type === 'apprentice' ? 'Uczeń / młodociany' : 'Pracownik'}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between pub-small">
                    <span>Urlop dostępny</span>
                    <b style={{ color: 'var(--pub-text)' }}>{worker.vacation_days_available} dni</b>
                  </div>
                  <div style={{ height: 4, background: 'var(--pub-line-soft)', borderRadius: 99, marginTop: 6, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 99, background: 'var(--pub-accent)',
                      width: worker.vacation_days_total > 0
                        ? `${(worker.vacation_days_available / worker.vacation_days_total) * 100}%`
                        : '0%',
                    }} />
                  </div>
                </button>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* BRAK PRACOWNIKÓW */}
      {workers.length === 0 && (
        <div className="pub-card text-center" style={{ padding: '40px 24px' }}>
          <h2 className="pub-h3">Dodaj pierwszego pracownika</h2>
          <p className="pub-muted mb-4">
            Dodaj pracownika ręcznie albo zaproś go do SIGMY, żeby składał wnioski z telefonu.
          </p>
          <div className="pub-actions justify-content-center">
            <button type="button" className="pub-btn" onClick={() => navigate('/employer-employees')}>
              <Plus size={16} className="me-2" />Dodaj pracownika
            </button>
            <button type="button" className="pub-link" style={{ background: 'none', border: 'none' }} onClick={() => navigate('/employer-link-requests')}>
              Zaproś konto pracownika
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployerHome;
